import { c as _c } from "react/compiler-runtime";
import OptimizedImage from '@/components/OptimizedImage';
import { motion } from 'framer-motion';
import { Move } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { DragEvent, MouseEvent, ReactNode, useState } from 'react';
import Loader from '@/components/Loader';
import ProcessingOverlay from '@/components/ProcessingOverlay';
import ViverseButton from '@/components/ViverseButton';
import IconCheck from '@/components/assets/icon_check.svg';
import IconPicture from '@/components/assets/icon_picture.svg';
import IconPlay from '@/components/assets/icon_play.svg';
import IconTrash from '@/components/assets/icon_trash.svg';
import IconUpload from '@/components/assets/icon_upload.svg';
import { Separator } from '@/components/ui/separator';
import { MAX_IMAGE_SIZE_MB, MAX_VIDEO_SIZE_MB } from '@/constants/file';
import { cn } from '@/lib/utils';
import { Asset } from '@/types/asset';
type EditMediaProps = {
  assets: Asset[];
  error: boolean;
  isSaving: boolean;
  uploadSlot: ReactNode;
  handleCancel: (e: MouseEvent<HTMLButtonElement>) => void;
  handleDelete: (index: number) => void;
  handleReorder: (fromIndex: number, toIndex: number) => void;
};
const MAX_ASSETS = 25;
function EditMedia(t0) {
  const $ = _c(65);
  const {
    assets,
    error,
    isSaving,
    uploadSlot,
    handleCancel,
    handleDelete,
    handleReorder
  } = t0;
  const t = useTranslations();
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = (_e, index) => {
      setDraggedIndex(index);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleDragStart = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setDraggedIndex(null);
      setDragOverIndex(null);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleDragEnd = t2;
  let t3;
  if ($[2] !== draggedIndex) {
    t3 = (e, index_0) => {
      e.preventDefault();
      if (draggedIndex !== null && draggedIndex !== index_0) {
        setDragOverIndex(index_0);
      }
    };
    $[2] = draggedIndex;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handleDragOver = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setDragOverIndex(null);
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const handleDragLeave = t4;
  let t5;
  if ($[5] !== draggedIndex || $[6] !== handleReorder) {
    t5 = (e_0, dropIndex) => {
      e_0.preventDefault();
      if (draggedIndex !== null && draggedIndex !== dropIndex) {
        handleReorder(draggedIndex, dropIndex);
      }
      setDraggedIndex(null);
      setDragOverIndex(null);
    };
    $[5] = draggedIndex;
    $[6] = handleReorder;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const handleDrop = t5;
  let t6;
  let t7;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      opacity: 0,
      y: -5
    };
    t7 = {
      opacity: 1,
      y: 0
    };
    t8 = {
      opacity: 0,
      y: 5
    };
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t6 = $[8];
    t7 = $[9];
    t8 = $[10];
  }
  let t9;
  if ($[11] !== isSaving) {
    t9 = isSaving && <div className="absolute inset-0 z-floating flex cursor-wait items-center justify-center"><Loader /></div>;
    $[11] = isSaving;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== error) {
    t10 = cn("body-2", {
      "text-vive-error": error
    });
    $[13] = error;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== t) {
    t11 = t("profile-page.photos-videos-restrictions", {
      videoSize: MAX_VIDEO_SIZE_MB,
      imageSize: MAX_IMAGE_SIZE_MB
    });
    $[15] = t;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== t10 || $[18] !== t11) {
    t12 = <p className={t10}>{t11}</p>;
    $[17] = t10;
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== assets || $[21] !== dragOverIndex || $[22] !== draggedIndex || $[23] !== handleDelete || $[24] !== handleDragOver || $[25] !== handleDrop || $[26] !== t) {
    let t14;
    if ($[28] !== dragOverIndex || $[29] !== draggedIndex || $[30] !== handleDelete || $[31] !== handleDragOver || $[32] !== handleDrop || $[33] !== t) {
      t14 = (asset, index_1) => <div key={index_1} draggable={true} onDragStart={e_1 => handleDragStart(e_1, index_1)} onDragEnd={handleDragEnd} onDragOver={e_2 => handleDragOver(e_2, index_1)} onDragLeave={handleDragLeave} onDrop={e_3 => handleDrop(e_3, index_1)} className={cn("relative aspect-[326/191] w-full cursor-move overflow-hidden rounded-2xl transition-all hover:scale-105 md:aspect-[306/180]", {
        "opacity-50": draggedIndex === index_1,
        "border-vive-brand-50 border-[3px]": dragOverIndex === index_1,
        "scale-105": dragOverIndex === index_1
      })}>{asset.image?.url && <><OptimizedImage src={asset.image.url} alt="cover" fill={true} sizes="(max-width: 768px) 100vw, (max-width: 1440px) 50vw, 25vw" className="object-cover" /><IconPicture className="absolute bottom-2 left-2 z-interactive size-7" /><div className="bg-vive-surface-normal/50 hover:bg-vive-primary-70 absolute top-2 left-2 z-interactive cursor-grab rounded-full p-2 transition-all hover:scale-105"><Move className="size-6" /></div><div className="bg-vive-surface-normal/50 hover:bg-vive-error absolute top-2 right-2 z-interactive grid size-10 cursor-pointer place-items-center rounded-full transition-all hover:scale-105" onClick={e_4 => {
            e_4.stopPropagation();
            handleDelete(index_1);
          }} onMouseDown={_temp}><IconTrash className="size-8" /></div></>}{asset.video?.url && <><video src={asset.video.url} className="size-full object-cover" autoPlay={true} muted={true} /><IconPlay className="fill-vive-primary-20 absolute bottom-2 left-2 z-interactive size-7" /><div className="bg-vive-surface-normal/50 hover:bg-vive-primary-70 absolute top-2 left-2 z-interactive cursor-grab rounded-full p-2 transition-all hover:scale-105"><Move className="size-6" /></div>{(asset.video.vodId && asset.video.isConverted || !asset.video.vodId && !asset.video.fileId) && <div className="bg-vive-surface-normal/50 hover:bg-vive-error absolute top-2 right-2 z-interactive grid size-10 cursor-pointer place-items-center rounded-full transition-all hover:scale-105" onClick={e_6 => {
            e_6.stopPropagation();
            handleDelete(index_1);
          }} onMouseDown={_temp2}><IconTrash className="size-8" /></div>}{asset.video.vodId && !asset.video.isConverted && <ProcessingOverlay animate={true} className="pointer-events-none absolute top-0 left-0" />}</>}{asset.upload?.uploading && <motion.div initial={{
          opacity: 0,
          x: -5
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: 5
        }} className="absolute inset-0 flex animate-pulse flex-col items-center justify-center gap-2 duration-300" style={{
          background: "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.5) 100%)"
        }}><IconUpload className="size-6" /><p className="body-2 flex items-center gap-1">{t("avatar-editor.submit-button.uploading")}{asset.upload?.progress}%</p><div className="absolute bottom-0 w-full"><motion.div className="bg-vive-primary-40 animate-pulse-glow h-2 rounded-lg" transition={{
              duration: 2,
              ease: "linear"
            }} initial={{
              width: 0
            }} animate={{
              width: `${asset.upload.progress}%`
            }} style={{
              background: "linear-gradient(270deg, #CCF0F9 0%, #00A0CB 51.77%, #0589C9 100%)"
            }} /></div></motion.div>}</div>;
      $[28] = dragOverIndex;
      $[29] = draggedIndex;
      $[30] = handleDelete;
      $[31] = handleDragOver;
      $[32] = handleDrop;
      $[33] = t;
      $[34] = t14;
    } else {
      t14 = $[34];
    }
    t13 = assets.map(t14);
    $[20] = assets;
    $[21] = dragOverIndex;
    $[22] = draggedIndex;
    $[23] = handleDelete;
    $[24] = handleDragOver;
    $[25] = handleDrop;
    $[26] = t;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const t14 = assets.length < MAX_ASSETS && uploadSlot;
  let t15;
  if ($[35] !== t13 || $[36] !== t14) {
    t15 = <div className="1.5xl:grid-cols-4 grid w-full grid-cols-1 gap-3 md:grid-cols-2 md:gap-5">{t13}{t14}</div>;
    $[35] = t13;
    $[36] = t14;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Separator className="bg-vive-primary-60 w-full" />;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  const t17 = `${assets.length}/${MAX_ASSETS}`;
  let t18;
  if ($[39] !== t) {
    t18 = t("profile-page.current-assets");
    $[39] = t;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t17 || $[42] !== t18) {
    t19 = <p className="body-2">{t17} {t18}</p>;
    $[41] = t17;
    $[42] = t18;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <IconCheck className="mr-2 size-6" />;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== t) {
    t21 = t("profile-page.banner.save");
    $[45] = t;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== t21) {
    t22 = <ViverseButton className="h-[48px]" type="submit">{t20}{t21}</ViverseButton>;
    $[47] = t21;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== t) {
    t23 = t("button.cancel");
    $[49] = t;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== handleCancel || $[52] !== t23) {
    t24 = <ViverseButton variant="secondary" className="h-[48px]" onClick={handleCancel}>{t23}</ViverseButton>;
    $[51] = handleCancel;
    $[52] = t23;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== t22 || $[55] !== t24) {
    t25 = <div className="flex flex-col items-end gap-4 md:flex-row md:items-center">{t22}{t24}</div>;
    $[54] = t22;
    $[55] = t24;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== t19 || $[58] !== t25) {
    t26 = <div className="flex flex-col gap-6">{t16}<div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">{t19}{t25}</div></div>;
    $[57] = t19;
    $[58] = t25;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  let t27;
  if ($[60] !== t12 || $[61] !== t15 || $[62] !== t26 || $[63] !== t9) {
    t27 = <motion.div initial={t6} animate={t7} exit={t8} className="relative flex w-full flex-col gap-6 p-6">{t9}{t12}{t15}{t26}</motion.div>;
    $[60] = t12;
    $[61] = t15;
    $[62] = t26;
    $[63] = t9;
    $[64] = t27;
  } else {
    t27 = $[64];
  }
  return t27;
}
function _temp2(e_7) {
  return e_7.stopPropagation();
}
function _temp(e_5) {
  return e_5.stopPropagation();
}
export default EditMedia;